import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { BehaviorSubject, merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';

import { DeviceInformation, DeviceLocation } from '@rocksoft/mdm-sdk-v2';

import { AsyncPipe } from '@angular/common';
import { DeviceLocationStoreActions, DeviceLocationStoreSelectors } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { PaneComponent } from '@rocksoft/cdk';
import { isOsVersionAtLeast } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-device-location',
  templateUrl: './device-location.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, CommandSpinnerComponent, FaIconComponent, AsyncPipe],
})
export class DeviceLocationComponent {
  private readonly store = inject(Store);
  private readonly appleMdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);

  private subscription: Subscription;

  readonly apple = input.required<DeviceInformation>();
  location: Observable<DeviceLocation | undefined> = this.store.select(
    DeviceLocationStoreSelectors.selectDeviceLocationByUdid
  );

  private _isProcessing = new BehaviorSubject(false);
  isEnabled: Observable<boolean | null>;

  private readonly _udidSignal = computed(() => this.apple().udid);
  private _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'DeviceLocation'))
    )
  );

  readonly isLostModeEnabled = computed(() => this.apple().isMDMLostModeEnabled ?? false);
  readonly isSupervised = computed(() => this.apple()?.isSupervised ?? false);
  readonly hasSupportedOS = computed(() => isOsVersionAtLeast(this.apple().osVersion ?? '', '10.3.0'));

  enabled = true;

  constructor() {
    this.isEnabled = this.isProcessing.pipe(
      map((isProcessing) => {
        return (
          this.isSupervised() &&
          this.isLostModeEnabled() &&
          !isProcessing &&
          (this.hasSupportedOS() == null || this.hasSupportedOS())
        );
      })
    );

    this.subscription = this.commandProgress$
      .pipe(
        filter((progress) => progress.status === 'Acknowledged'),
        distinctUntilChanged()
      )
      .subscribe((progress) => {
        this.store.dispatch(
          DeviceLocationStoreActions.DeviceLocationResolverActions.deviceLocationRequested({ udid: progress.udid })
        );
      });
  }

  get isProcessing() {
    return this._isProcessing;
  }

  get isSupported() {
    return this.isSupervised() && (this.hasSupportedOS() == null || this.hasSupportedOS());
  }

  deviceLocation() {
    const udid = this.apple()?.udid;
    if (!udid) {
      return;
    }

    this._isProcessing.next(true);
    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DeviceLocation',
    };
    this._commandProgress.next(command);
    this.appleMdmService
      .deviceLocation(udid)
      .pipe(
        finalize(() => {
          this._isProcessing.next(false);
        })
      )
      .subscribe();
  }

  map(location: DeviceLocation) {
    if (!location?.latitude || !location?.longitude) {
      return;
    }

    window.open(
      `http://www.google.com/maps/place/${location.latitude.toString()},${location.longitude.toString()}`,
      '_blank'
    );
  }
}
